'use client';
import { useState } from 'react';
import dynamic from 'next/dynamic';
import { Card } from 'primereact/card';
import { Template } from './template';
import { PriceInfo, RatingBadge } from './Price';
import { classNames } from 'primereact/utils';
import { useRouter } from 'next/navigation';
import { BuyTemplate } from '../buy.template';
import { DeliveryInfo } from '@/_components/shipping/DeliveryInfo';
import { AICircle } from '@/_components/AICircle';
import { getProductTechDetails, getProductFeatures } from '@/app/actions/productDetails';
import { CardImageSlider } from './cardImageSlider';
import Link from 'next/link';

// Loaded on demand — not part of the initial JS bundle
const DetailsView   = dynamic(() => import('./techDetails').then(m => ({ default: m.DetailsView })), { ssr: false });
const VideoDialog   = dynamic(() => import('./VideoPlayer').then(m => ({ default: m.VideoDialog })), { ssr: false });
const YouTubePayler = dynamic(() => import('./YouTubePlayerAdvanced').then(m => ({ default: m.YouTubePayler })), { ssr: false });

const CARD_IMG_H = 200;

const VIDEO_EXTS = /\.(mp4|webm|mov|m4v|avi)(\?|$)/i;
const YT_REGEX   = /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;

const getYouTubeId = (url) => url?.match(YT_REGEX)?.[1] ?? null;
const isYouTubeUrl = (url) => !!(url && (url.includes('youtube.com') || url.includes('youtu.be')));
const isCustomVideoUrl = (url) => !!(url && VIDEO_EXTS.test(url));

// ── Card-level image only ─────────────────────────────────────────────────────
const ProductCardMedia = ({ coverImage, slug, productTitle, isNew, priority = false }) => (
    <div className="border-0 border-50 border-round-xl p-2 hover:border-100 bg-white">
        <div className="ennix-card-media" style={{ position: 'relative', height: CARD_IMG_H, overflow: 'hidden', borderRadius: 8 }}>
            {isNew && (
                <span style={{
                    position: 'absolute', top: 0, left: 8, zIndex: 10,
                    background: '#ef4444', color: '#fff',
                    fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.05em',
                    padding: '4px 8px', borderRadius: 6,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                    pointerEvents: 'none',
                }}>
                    NEW
                </span>
            )}
            {coverImage ? (
                <Link href={`/${slug}`} style={{ display: 'block', width: '100%', height: '100%' }}>
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                        src={coverImage}
                        alt={productTitle || 'Product image'}
                        loading={priority ? 'eager' : 'lazy'}
                        fetchPriority={priority ? 'high' : undefined}
                        decoding="async"
                        style={{ width: '100%', height: CARD_IMG_H, objectFit: 'contain', display: 'block' }}
                    />
                </Link>
            ) : (
                <div style={{
                    width: '100%', height: CARD_IMG_H, background: '#f1f5f9',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 8,
                }}>
                    <i className="pi pi-box" style={{ fontSize: '2rem', color: '#94a3b8' }} />
                </div>
            )}
        </div>
    </div>
);

// ─────────────────────────────────────────────────────────────────────────────

const cardPt = {
    root: { className: 'surface-0 pb-0 border-round-xl', style: { boxShadow: '0 6px 28px rgba(0,0,0,0.14), 0 2px 8px rgba(0,0,0,0.08)' } },
    body: { className: 'pb-0 pt-0 product-card surface-50 border-top-1 border-200 pt-3 pb-2 border-round-md border-noround-top' },
    content: { className: 'p-0' },
    title: { className: 'text-xs font-bold' },
};

export const ProductCard = ({ data, priority = false }) => {
    const [isDetails,        setIsDetails]        = useState(false);
    const [detailsEverOpen,  setDetailsEverOpen]  = useState(false);
    const [loadingDetails,   setLoadingDetails]   = useState(false);
    const [playVideo,        setPlayVideo]        = useState(false);
    const router = useRouter();
    const goToDetails = () => router.push(`/${data?.slug}`);

    const videoUrl  = data?.videoUrl;
    const isYT      = isYouTubeUrl(videoUrl);
    const isCustom  = !isYT && isCustomVideoUrl(videoUrl);
    const hasVideo  = isYT || isCustom;
    const youtubeId = isYT ? getYouTubeId(videoUrl) : null;

    const doOpenDetails = async () => {
        if (loadingDetails) return;
        const productId = data?._id;
        if (!productId) return;

        setLoadingDetails(true);
        setDetailsEverOpen(true);
        setIsDetails({
            title: data?.title, coverImage: data?.coverImage, slug: data?.slug,
            avgRating: data?.averageRating, reviewCount: data?.reviewCount,
            techDetails: null, features: null, userManualUrl: null, loading: true,
        });

        const [techRes, featRes] = await Promise.all([
            getProductTechDetails(productId),
            getProductFeatures(productId),
        ]);

        setIsDetails({
            title: data?.title, coverImage: data?.coverImage, slug: data?.slug,
            avgRating: data?.averageRating, reviewCount: data?.reviewCount,
            techDetails: techRes.data, features: featRes.data,
            userManualUrl: techRes.userManualUrl, loading: false,
        });
        setLoadingDetails(false);
    };
    return (
        <div>
            {detailsEverOpen && <DetailsView isDetails={isDetails} setIsDetails={setIsDetails} />}
            {playVideo && (
                <VideoDialog visible onHide={() => setPlayVideo(false)} title={data?.title} slug={data?.slug}>
                    {isYT ? (
                        youtubeId
                            ? <YouTubePayler videoId={youtubeId} />
                            : <p style={{ color: '#fb923c', padding: '16px', fontSize: '0.85rem' }}>Video not available.</p>
                    ) : (
                        /* eslint-disable-next-line jsx-a11y/media-has-caption */
                        <video controls autoPlay style={{ width: '100%', maxHeight: '62vh', borderRadius: 12, background: '#000', boxShadow: '0 20px 60px rgba(0,0,0,0.6)' }}>
                            <source src={videoUrl} />
                            Your browser does not support the video tag.
                        </video>
                    )}
                </VideoDialog>
            )}
            <Card
                pt={cardPt}
                title={
                    <Link href={`/${data?.slug}`} className="ennix-card-title" style={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'flex-start', gap: 4 }}>
                        <Template.details data={data?.title} char={90} />
                        <i className="pi pi-external-link" style={{ fontSize: '0.6rem', color: 'var(--primary-color)', flexShrink: 0, marginTop: 3 }} />
                    </Link>
                }
                header={
                    <div className="p-3 relative -mb-2" style={{ position: 'relative' }}>
                        {hasVideo ? (
                            <CardImageSlider
                                coverImage={data?.coverImage}
                                videoCover={data?.videoCover}
                                slug={data?.slug}
                                productTitle={data?.title}
                                onPlayVideo={() => setPlayVideo(true)}
                                isNew={data?.isNewProduct}
                                priority={priority}
                            />
                        ) : (
                            <ProductCardMedia
                                coverImage={data?.coverImage}
                                slug={data?.slug}
                                productTitle={data?.title}
                                isNew={data?.isNewProduct}
                                priority={priority}
                            />
                        )}
                        <div style={{ position: 'absolute', bottom: 16, right: 16, zIndex: 10 }}>
                            <AICircle title={data?.title} coverImage={data?.coverImage} productId={data?._id} price={data?.price} mrp={data?.mrp} offer={data?.offer} coupons={data?.coupons} product={data} size={28} />
                        </div>
                    </div>
                }
            >
                <div className="relative p-2 mt-2 mb-2 flex align-items-start justify-content-between border-top-1 border-300 border-bottom-1">
                    <PriceInfo offer={data?.offer} price={data?.price} mrp={data?.mrp} coupons={data?.coupons} />
                    <div className="absolute right-0">
                        <RatingBadge avg={data?.averageRating} count={data?.reviewCount} product={data} />
                    </div>
                </div>

                <div className="flex justify-content-between gap-1 mt-2 mb-1">
                    <button
                        type="button"
                        onClick={doOpenDetails}
                        disabled={loadingDetails}
                        aria-label={loadingDetails ? 'Loading tech specs…' : 'View tech specs'}
                        style={{
                            flex: 1, display: 'flex', flexDirection: 'column',
                            alignItems: 'center', gap: '4px', padding: '6px 4px',
                            background: loadingDetails ? '#f0fdf4' : '#f8fafc',
                            border: `1px solid ${loadingDetails ? '#86efac' : '#e2e8f0'}`,
                            borderRadius: '8px', cursor: loadingDetails ? 'not-allowed' : 'pointer',
                            minWidth: 0, transition: 'all 0.15s', opacity: loadingDetails ? 0.85 : 1,
                        }}
                    >
                        <span style={{ fontSize: '0.65rem', fontWeight: 600, color: '#334155', lineHeight: 1.2, textAlign: 'center' }}>
                            Tech Specs
                        </span>
                        {loadingDetails
                            ? <i className="pi pi-spin pi-spinner" style={{ fontSize: '0.8rem', color: '#3b82f6' }} aria-hidden="true" />
                            : <i className="pi pi-list"            style={{ fontSize: '0.8rem', color: '#3b82f6' }} aria-hidden="true" />
                        }
                    </button>
                    <button
                        type="button"
                        onClick={goToDetails}
                        aria-label="View product details"
                        style={{
                            flex: 1, display: 'flex', flexDirection: 'column',
                            alignItems: 'center', gap: '4px', padding: '6px 4px',
                            background: '#f8fafc', border: '1px solid #e2e8f0',
                            borderRadius: '8px', cursor: 'pointer', minWidth: 0,
                        }}
                    >
                        <span style={{ fontSize: '0.65rem', fontWeight: 600, color: '#334155', lineHeight: 1.2, textAlign: 'center' }}>View Details</span>
                        <i className="pi pi-eye" style={{ fontSize: '0.8rem', color: '#334155' }} />
                    </button>
                    <DeliveryInfo mini />
                </div>

                {data?.outOfStock && (
                    <div className={classNames('mt-2 pt-2 pb-2 pl-0 border-300 gap-2 flex flex-row buy-now border-top-1')}>
                        <BuyTemplate.OutOfStockInfo />
                    </div>
                )}
                {!data?.outOfStock && (
                    <div className={classNames('mt-2 pt-2 pb-2 pl-0 border-300 gap-2 flex flex-row buy-now border-top-1')}>
                        <BuyTemplate.AddToCart product={data} />
                        <BuyTemplate.BuyNowButton product={data} />
                    </div>
                )}
            </Card>
        </div>
    );
};
